import { usePusher } from '@/hooks/usePusher';
import { Text } from '@chakra-ui/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

/** A typing line disappears this long after the last signal. */
export const CREATOR_TYPING_VISIBLE_MS = 5000;

/**
 * "Léa écrit..." above the composer while the creator writes in the creator
 * space: shown on her signal, hidden five seconds later or when her message
 * arrives (`newestMessageId` moves).
 */
export function CreatorTypingIndicator({
  conversationId,
  creatorName,
  newestMessageId,
}: {
  conversationId: string;
  creatorName: string;
  newestMessageId: string | null;
}) {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const onTyping = useCallback((data: { side?: string }) => {
    if (data?.side !== 'creator') return;
    setVisible(true);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setVisible(false), CREATOR_TYPING_VISIBLE_MS);
  }, []);

  usePusher({ channelName: `private-conversation-${conversationId}`, eventName: 'typing', callback: onTyping });

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    setVisible(false);
  }, [newestMessageId]);

  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
  }, []);

  if (!visible) return null;
  return (
    <Text fontSize="xs" color="gray.500" px={2} pb={1} data-testid="creator-typing">
      {t('messages.thread.typing', { name: creatorName })}
    </Text>
  );
}
